import { AsyncPipe, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  input,
  output,
  inject,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import {
  AdvancedSettingService,
  AuthSevices,
  ClaimService,
  ClientService,
  InvoiceService,
  PaymentService,
  TimeconversionService,
} from '@fitness-force/api';
import { AppErrors } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';
import type {
  Claim,
  Client,
  Invoice,
  InvoiceClient,
  Payment,
  PaymentSignUp,
  STAFFINTERFACE,
} from '@fitness-force/models';
import { AssignedToComponent } from '@fitness-force/common/assigned-to';
import { ErrorsComponent } from '@fitness-force/errors';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { COMPANY_INFO, STAFF_STATE, TENANTS_INFO } from '@fitness-force/state';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';

import { DrawerModule } from 'primeng/drawer';
import { StyleClassModule } from 'primeng/styleclass';
import { TableModule } from 'primeng/table';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatestWith,
  retryWhen,
  scan,
  Subject,
  switchMap,
  takeUntil,
  throwError,
  timer,
} from 'rxjs';
import { OtherPaymentComponent } from '@fitness-force/common/payment';

import { CurrencyformatPipe } from '@fitness-force/pipes/currency';

// import { DatePickerModule } from 'primeng/datepicker';
import { Roles } from '@fitness-force/enum';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { DatePickerModule } from 'primeng/datepicker';

@Component({
  selector: 'payment-summary',
  templateUrl: './payment-summary.component.html',
  styleUrls: ['./payment-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [
    FormsModule,
    DrawerModule,
    AvatarModule,
    AvatarGroupModule,
    ReactiveFormsModule,
    ErrorsComponent,
    ErrorMsgComponent,
    StyleClassModule,
    DialogModule,
    TableModule,
    ButtonModule,
    OtherPaymentComponent,
    CurrencyformatPipe,
    AssignedToComponent,
    DatePickerModule,
    TranslateModule,
    AsyncPipe,
    TitleCasePipe,
    ShortNamePipe,
  ],
})
export class PaymentSummaryComponent implements OnInit, OnDestroy {
  private fb = inject(UntypedFormBuilder);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private claimService = inject(ClaimService);
  authService = inject(AuthSevices);
  clientService = inject(ClientService);
  private invoiceService = inject(InvoiceService);
  private paymentService = inject(PaymentService);
  private timeService = inject(TimeconversionService);
  private advanceSettingService = inject(AdvancedSettingService);
  private featureFlagService = inject(FeatureFlagService);
  private cd = inject(ChangeDetectorRef);

  readonly showPaymentSummary = input(false, { alias: 'showpaymentsummary' });
  readonly getInvoiceById$ = input<Observable<InvoiceClient>>(undefined, {
    alias: 'invoiceDetailById',
  });
  toggleBlockClientDialog$ = new BehaviorSubject<boolean | null>(false);
  private readonly destroy$ = new Subject<void>();
  readonly closeSidePopup = output<{
    hideSidebar: boolean;
    clientSignupUUID: string;
  }>();

  getAccessTenantList$: Observable<any>;
  claim$: Observable<Claim[]> = new Observable();
  invoiceDetail$: Observable<Invoice> = new Observable();
  showErrorPopup = new BehaviorSubject(false);
  otherPaymentsModal$ = new BehaviorSubject(false);
  error = new BehaviorSubject(null as AppErrors | null);
  totalBalance$ = new BehaviorSubject(0);
  client$: Observable<Client> = new Observable();
  clientCreditBalance = 0;

  tenantId = 0;
  clientId = 0;
  invoiceId = 0;
  currentUserDetails: any;
  invoice: Invoice;
  inv: Invoice[] = [];
  totalBalance = 0;
  errorMessages = [] as any;
  isFormSubmitting = false;

  backedDatedDays: any = 0;
  disableDatesUpTo: Date = new Date();

  PaymentSummaryForm: UntypedFormGroup = new UntypedFormGroup({});
  _selectedstaff = new BehaviorSubject<number>(0);
  _filterStaff = new BehaviorSubject<number[]>([0]);
  _sessionOwner = new BehaviorSubject<number[]>([0]);
  showCreditBalance = false;

  paymentDateForm: UntypedFormGroup = new UntypedFormGroup({
    paymentDate: new UntypedFormControl(new Date(), [Validators.required]),
  });

  componentInit() {
    const companyInfoDetail$: Observable<any> = this.store.select(COMPANY_INFO);
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);
    this.claim$ = this.claimService.GetClaim();
    companyInfoDetail$.subscribe((responseData) => {
      this.translate.setDefaultLang(responseData?.selectedLang?.LANG_CODE);
      this.translate.use(responseData?.selectedLang?.LANG_CODE);
    });

    this.currentUserDetails = this.authService.decodedToken;
  }

  ngOnInit(): void {
    const invoice$ = this.getInvoiceById$();
    if (!invoice$) {
      return;
    }
    invoice$.subscribe((inv: InvoiceClient) => {
      this.clientId = inv.client_id;
      this.tenantId = inv.tenant_id;
      this.invoiceId = inv.invoice_id;
      this.componentInit();
      this.SetFormFields();
      this.getAdvanceSetting();
      this.getClientCreditBalance();
      this.filterStaff();
    });

    this.featureFlagService
      .getFlag$('customer_credit_balance')
      .pipe(takeUntil(this.destroy$))
      .subscribe((customerCreditBalance) => {
        this.showCreditBalance = customerCreditBalance;
        this.cd.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getClientCreditBalance() {
    this.clientService
      .getClientCreditBalanceByTenantId(this.clientId, this.tenantId)
      .subscribe({
        next: (res: any) => {
          this.clientCreditBalance = res;
        },
      });
  }

  getAdvanceSetting() {
    const tenant_settings = this.advanceSettingService.getAdvanceSetting(
      this.tenantId,
      'backdated_billing_setting',
    );
    if (tenant_settings) {
      tenant_settings.subscribe({
        next: (res) => {
          this.backedDatedDays = res.value;
          const currentDate = new Date();
          this.disableDatesUpTo = new Date(
            currentDate.setDate(currentDate.getDate() - this.backedDatedDays),
          );
        },
      });
    }
  }

  SetFormFields() {
    this.PaymentSummaryForm = this.fb.group({
      billingOwner: new UntypedFormControl('', [Validators.required]),
    });
    this.BindInvoice();
    this.BindClientData();
  }

  BindClientData() {
    if (this.clientId > 0) {
      this.client$ = this.clientService.getClientById(this.clientId);
    }
  }

  BindInvoice() {
    if (this.invoiceId > 0) {
      this.invoiceDetail$ = this.invoiceService.getInvoice(
        this.clientId,
        this.invoiceId,
      );
      this.invoiceDetail$.subscribe({
        next: (invoice: Invoice) => {
          this.totalBalance = invoice.total_balance_due_in_minor_currency;
          this.totalBalance$.next(invoice.total_balance_due_in_minor_currency);
          this.inv.push(invoice);
        },
        error: (error: AppErrors) => {
          this.handleError(error);
        },
      });
    }
  }

  filterStaff() {
    let claim: Claim[] = {} as Claim[];
    let staffs: STAFFINTERFACE[] = [];
    const validStaff: number[] = [];
    this.claim$
      .pipe(combineLatestWith(this.store.select(STAFF_STATE)))
      .subscribe(([objClaim, objStaff]) => {
        claim = objClaim;
        staffs = objStaff?.staffList || [];

        if (staffs.length > 0) {
          staffs.forEach((stf) => {
            if (
              stf.role == Roles.Admin ||
              stf.role.toString() == Roles[Roles.Admin] ||
              stf.role == Roles.FFAdmin ||
              stf.role.toString() == Roles[Roles.FFAdmin] ||
              claim.filter(
                (o) =>
                  o.id == stf.claim_template_id &&
                  (o.can_admin_billing == true || o.can_do_billing == true),
              ).length > 0
            )
              validStaff.push(stf.id);
          });
          this._filterStaff.next(validStaff);
        }
        if (this.authService.currentUserDetails()) {
          const currentUserDetails = this.authService.currentUserDetails();
          if (
            currentUserDetails.role.toLowerCase() == 'admin' ||
            currentUserDetails.can_do_billing == 'true' ||
            currentUserDetails.can_admin_billing == 'true'
          ) {
            this._selectedstaff.next(Number(this.authService.loggedInStaffId));
          }
        }
      });
  }

  handleError(error: any) {
    this.error.next(error);
    this.showErrorPopup?.next(true);
  }

  HideErrorPopup(event: any) {
    this.showErrorPopup?.next(false);
    this.errorMessages = [];
    this.error.next(null);
  }

  closeBillingSideBar() {
    this.closeSidePopup.emit({ hideSidebar: false, clientSignupUUID: '' });
  }

  clientStatus(client: any): string {
    if (client && client.client_status)
      return this.clientService.getClientStatusHtml(
        client.client_status.toString(),
      );
    else return '';
  }

  showPaymentModule() {
    this.otherPaymentsModal$.next(true);
  }

  HideOtherPaymentDialog(evt: any) {
    if (evt.paymentSignUp)
      this.SavePaymentUUID(evt.paymentSignUp as PaymentSignUp, true);
    else this.otherPaymentsModal$.next(false);
  }

  SavePaymentUUID(paymentSignup: PaymentSignUp, isOnline: boolean) {
    const payment = {} as Payment;
    payment.is_offline_payment = isOnline;
    payment.payment_date = this.timeService.ConvertLocalDateTimeToUTC(
      this.paymentDate.value,
    );
    payment.offline_payment = paymentSignup;
    payment.collected_at_center = this.tenantId;
    payment.sales_rep_id = this.billingOwner.value.id;
    this.isFormSubmitting = true;
    this.paymentService
      .makePayment(this.tenantId, this.clientId, this.invoiceId, payment)
      .pipe(
        retryWhen((errors) =>
          errors.pipe(
            // Check for the specific error condition that should trigger a retry
            scan((count, error) => {
              if (count < 2 && error && error.originalError.error) {
                if (
                  Array.isArray(error.originalError.error) &&
                  error.originalError.error[0].errorCode ===
                    'paymentamountgreaterthaninvoiceamount'
                ) {
                  payment.offline_payment.amount_in_minor_currency =
                    payment.offline_payment.rounded_amount_in_minor_currency!;
                  return count + 1; // Increment the retry count
                } else if (
                  error.originalError.error.errorCode ===
                  'paymentamountgreaterthaninvoiceamount'
                ) {
                  payment.offline_payment.amount_in_minor_currency =
                    payment.offline_payment.rounded_amount_in_minor_currency!;
                  return count + 1; // Increment the retry count
                } else {
                  throw error;
                }
              }
              throw error;
            }, 0),
            switchMap((retryCount) => timer(100)),
          ),
        ),
        catchError((error) => {
          return throwError(error);
        }),
      )
      .subscribe({
        next: (_objResponse: any) => {
          this.otherPaymentsModal$.next(false);
          this.isFormSubmitting = false;
          this.closeSidePopup.emit({ hideSidebar: true, clientSignupUUID: '' });
          this.closeSidePopup.emit({
            hideSidebar: true,
            clientSignupUUID: _objResponse.paymentLinkUuid,
          });
        },
        error: (error) => {
          this.otherPaymentsModal$.next(false);
          this.isFormSubmitting = false;
          this.error.next(error);
          this.showErrorPopup?.next(true);
        },
      });
  }

  get billingOwner() {
    return this.PaymentSummaryForm.controls.billingOwner as UntypedFormControl;
  }

  get paymentDate() {
    return this.paymentDateForm.controls.paymentDate as UntypedFormControl;
  }
}
